// @flow
import { get, indexOf } from 'lodash';
import { formatBytes } from './formats';
import ptBrGlobal from '../../i18n/pt-br/global';

export const wrapValue = (defaultValue: any, value: any, uncontrolled: any) => (defaultValue || uncontrolled ? ({ defaultValue }) : ({ value }));

export const isPtBR = (lng?: string) => String(lng || window.i18n.language).toLowerCase() === 'pt' || String(lng || window.i18n.language).toLowerCase() === 'pt-br';

// 1Password, LastPass and Dashlane ignore autocomplete="off" and keep offering
// identity fills (name, birthdate, address, phone, e-mail) over patient fields.
// These vendor attributes are the only reliable way to opt a field out.
//
// Deliberately no autoComplete here: it does nothing to the password managers,
// and "off" is the one value the browsers' own form history does respect —
// setting it took away the suggestion dropdown people rely on when they type
// the same appointment notes over and over. Fields that really want the
// browser silenced too (SearchInput) pass autoComplete="off" themselves.
export const ignoreAutofillProps = {
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-form-type': 'other',
};

export function memorySizeOf(obj: any) {
  let bytes = 0;
  // eslint-disable-next-line
  function sizeOf(obj: any) {
    if (obj !== null && obj !== undefined) {
      switch (typeof obj) {
        case 'number':
          bytes += 8;
          break;
        case 'string':
          bytes += obj.length * 2;
          break;
        case 'boolean':
          bytes += 4;
          break;
        case 'object':
          const objClass: any = Object.prototype.toString.call(obj).slice(8, -1);
          if (objClass === 'Object' || objClass === 'Array') {
            for (const key in obj) {
              // eslint-disable-next-line
              if (!obj.hasOwnProperty(key)) continue;
              sizeOf(obj[key]);
            }
          } else bytes += obj.toString().length * 2;
          break;
        default: break;
      }
    }
    return bytes;
  }
  return formatBytes(sizeOf(obj));
}


export const notifyBug = (message: any) => {
  if (window.tron && window.tron.error) {
    window.tron.error(message);
  }
};

// For an upload's signing URL: new on every call, so no cached signature is reused, and digits
// only, so the URL can't end in "pdf" (Api.get would then ask for bytes instead of JSON)
export const uploadNonce = (): string => `${Date.now()}${Math.floor(Math.random() * 1e6)}`;

export const globalT = (key: string, fallback: string, options?: Object) => (
  get(window, ['i18n', 't']) ? window.i18n.t(`global:${key}`, options) : fallback
);

// globalT outside React (requests, thunks): before i18n is set up, the Portuguese text from the same file
export const globalTOrPtBr = (key: string) => globalT(key, ptBrGlobal[key]);

// The page on screen, e.g. "/paciente/7/cadastro", for code outside React. The app routes by the hash, and the
// store's `router` isn't kept up to date (the Router has a history of its own; the store's comes from persistence).
export const currentPath = (): string => String(window.location.hash || '').replace(/^#/, '').split('?')[0];

export const reportError = (error: any) => {
  if (window.navigator.onLine) {
    notifyBug({
      errorType: 'api-request-error:fn-reportError',
      error,
    });

    const errorMessage = get(error, 'message', globalT('error/request', 'Ocorreu um erro ao executar esta requisição'));

    return {
      type: 'toasts.OPEN_TOAST',
      data: {
        type: 'error', text: errorMessage, close: true, error,
      },
    };
  } else {
    // offline: it closes as soon as the connection comes back (Toast)
    return {
      type: 'toasts.OPEN_TOAST',
      data: {
        type: 'warning', text: globalT('error/request_internet', 'Ocorreu um erro. Verifique sua conexão com a internet.'), close: true, offline: true, error,
      },
    };
  }
};

export const isCordovaApp = ():boolean => window.isCordovaApp;

// The iOS app, on an iPhone or an iPad (an iPad's WebView can report navigator.platform as "MacIntel")
export const isIOSApp = (): boolean => Boolean(window.isCordovaApp) && get(window, ['cordova', 'platformId']) === 'ios';

// ios_review from the API's app-version header: the iOS versions in App Store review, comma-separated,
// or "*" for every iOS version. Only the iOS app can be in review.
export const isStoreReviewBuild = (reviewVersions: ?string, version: ?string = window.APP_VERSION): boolean => {
  if (!isIOSApp() || !reviewVersions || !version) return false;
  return String(reviewVersions).split(',').map((v) => v.trim()).some((v) => v === '*' || v === String(version));
};

export const getPlatform = () => {
  const iosPlatformInstances = ['iPhone', 'iPad'];
  if (!window.isCordovaApp) {
    return 'web';
  } else if (window.navigator && (indexOf(iosPlatformInstances, window.navigator.platform) > -1)) {
    return 'ios';
  }
  return 'android';
};

export const isBetaApp = () => get(window, 'isBetaApp', false);
